import { isPlatformBrowser } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  PLATFORM_ID,
  computed,
  inject,
  signal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ActivatedRoute, RouterLink } from '@angular/router'

import { TranslatePipe } from '@ngx-translate/core'
import { EMPTY, catchError, switchMap, takeWhile, timer } from 'rxjs'

import { ArticlesService } from '../../../../api/articles-service/articles.service'
import { getArticleIdFromRoute } from '../../../auth/util/article-purchase-route.utils'
import { ArticlePurchaseAccountShellComponent } from '../article-purchase-account-shell.component'

const PURCHASE_STATUS_POLL_INTERVAL_MS = 10000

@Component({
  selector: 'dhp-article-purchase-success',
  templateUrl: './article-purchase-success.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, TranslatePipe, ArticlePurchaseAccountShellComponent],
})
export class ArticlePurchaseSuccessComponent {
  private readonly route = inject(ActivatedRoute)
  private readonly articlesService = inject(ArticlesService)
  private readonly destroyRef = inject(DestroyRef)
  private readonly platformId = inject(PLATFORM_ID)

  readonly articleId = signal<number | null>(null)
  readonly articleTitle = signal<string | null>(null)
  readonly purchaseId = signal<number | null>(null)
  readonly isPaid = signal(false)
  readonly canOpenArticle = computed(
    () => this.articleId() !== null && this.isPaid()
  )

  constructor() {
    const routeArticleId =
      getArticleIdFromRoute(this.route) ??
      this.parseRouteNumber(this.route.snapshot.queryParamMap.get('articleId'))

    this.articleId.set(routeArticleId)
    this.purchaseId.set(
      this.parseRouteNumber(this.route.snapshot.queryParamMap.get('purchaseId'))
    )

    if (isPlatformBrowser(this.platformId)) {
      this.startPolling()
    }
  }

  private parseRouteNumber(value: string | null): number | null {
    if (!value) {
      return null
    }

    const parsed = parseInt(value, 10)
    return isNaN(parsed) ? null : parsed
  }

  private startPolling(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return
    }

    const purchaseId = this.purchaseId()
    if (purchaseId === null) {
      return
    }

    timer(0, PURCHASE_STATUS_POLL_INTERVAL_MS)
      .pipe(
        switchMap(() =>
          this.articlesService.getUserArticlePurchaseById(
            purchaseId,
            'network-only'
          )
        ),
        takeWhile(purchase => !purchase?.paid_at, true),
        catchError((error: unknown) => {
          console.error('Error polling user article purchase status', error)
          return EMPTY
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(purchase => {
        if (purchase?.article?.id) {
          this.articleId.set(purchase.article.id)
        }

        if (purchase?.article?.title) {
          this.articleTitle.set(purchase.article.title)
        }

        if (purchase?.paid_at) {
          this.isPaid.set(true)
        }
      })
  }
}
